/**
 * Generate a name that does not collide with any existing bot name.
 *
 * Returns `base` when it is free, otherwise appends the smallest integer
 * suffix (starting at 2) that yields a free name: "New Bot", "New Bot 2",
 * "New Bot 3", ... Comparison is case-insensitive and trims surrounding
 * whitespace so " new bot " collides with "New Bot".
 *
 * See FE #185 — "+ New Agent" previously hardcoded "New Bot", producing
 * multiple identically-named agents.
 */
export function generateUniqueBotName(existingNames: (string | undefined | null)[], base = "New Bot"): string {
  const taken = new Set(
    (existingNames ?? [])
      .filter((n): n is string => typeof n === "string")
      .map((n) => n.trim().toLowerCase())
  );

  if (!taken.has(base.trim().toLowerCase())) {
    return base;
  }

  let suffix = 2;
  // Bounded loop guard: taken.size + 2 is always enough to find a free slot.
  const limit = taken.size + 2;
  while (suffix <= limit) {
    const candidate = `${base} ${suffix}`;
    if (!taken.has(candidate.trim().toLowerCase())) {
      return candidate;
    }
    suffix++;
  }
  // Unreachable in practice; kept as a defensive fallback.
  return `${base} ${suffix}`;
}
